import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button, Input } from '@/components/ui';
import { useProfileNameUpdate } from '@/features/profiles/api';
import { useProfilesData } from '@/features/user';
import { useScrollToTop } from '@/hooks/use-scroll-to-top';
import { usePageView } from '@/lib/analytics';

const verifyNameSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, 'Please enter a valid name')
    .max(100, 'Name is too long'),
});

type VerifyNameFormData = z.infer<typeof verifyNameSchema>;

/**
 * Full-screen blocking gate (mirrors ForceSelectProfile) shown when the
 * selected profile has `unverified_name === true`. Collects the student's name
 * and saves it via the no-OTP profile-name update. On success the profiles
 * query is invalidated, the flag clears, and this gate is dismissed.
 */
export default function VerifyName() {
  usePageView('Verify Student Name');
  useScrollToTop();

  const { selectedProfile } = useProfilesData();
  const { updateName, isUpdatingName } = useProfileNameUpdate();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<VerifyNameFormData>({
    resolver: zodResolver(verifyNameSchema),
  });

  const loading = isSubmitting || isUpdatingName;
  // The gate only renders once profiles have loaded, but guard anyway so we
  // never submit without a profile id to attach the name to.
  const canSubmit = Boolean(selectedProfile?.id) && !loading;

  const onSubmit = (data: VerifyNameFormData) => {
    if (!selectedProfile?.id) {
      return;
    }
    updateName({ id: selectedProfile.id, name: data.name });
  };

  return (
    <div className="min-h-screen bg-magical-moonlight-white flex flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-md text-center">
        <h1 className="text-2xl font-bold font-quicksand text-magical-moonlight-d4 mb-3">
          What&apos;s the student&apos;s name?
        </h1>
        <p className="text-sm text-magical-moonlight-d3 mb-12">
          Tell us the student&apos;s name to personalise your experience.
        </p>

        <form
          onSubmit={event => {
            event.preventDefault();
            void handleSubmit(onSubmit)(event);
          }}
          className="space-y-6 text-left"
        >
          <Input
            label="Student Name"
            type="text"
            placeholder="Enter your name"
            errorText={errors.name?.message}
            {...register('name')}
          />

          <div className="flex justify-center">
            <Button
              type="submit"
              variant="raised"
              size="lg"
              className="w-56"
              disabled={!canSubmit}
            >
              {loading ? 'Saving...' : 'Continue'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
